/**
 * Totals in more than one currency (docs/design/currency.md R5, CUR-2).
 *
 * The API never adds currencies together: a total is a headline in one
 * currency (the workspace's) plus `others`, each in its own currency. These
 * show the others beneath the headline, each formatted in its own currency
 * and said to be not converted, so nobody reads them as part of it.
 */
import { StatCard } from "./ui";
import { useTranslation } from "@/lib/i18n";
import { formatMoney } from "@/lib/money";
import type { CurrencyAmount, CurrencyFigure } from "@/lib/currency";

/** "Also $100.00 and €5.00, not converted", or nothing when there are none. */
export function OtherCurrencies({
  others,
}: {
  others: readonly CurrencyAmount[] | undefined;
}) {
  const { t, i18n } = useTranslation();
  if (!others || others.length === 0) return null;
  const amounts = others.map((o) => formatMoney(o.amount, o.currency));
  let joined: string;
  try {
    joined = new Intl.ListFormat(i18n.language, {
      type: "conjunction",
    }).format(amounts);
  } catch {
    joined = amounts.join(", ");
  }
  return (
    <div className="atlas-stat__meta" data-testid="other-currencies">
      {t("money.othersNotConverted", { amounts: joined })}
    </div>
  );
}

/** A stat card for a per-currency figure: the headline, the others beneath. */
export function MoneyStatCard({
  label,
  figure,
  meta,
  tone,
}: {
  label: string;
  figure: CurrencyFigure;
  meta?: string;
  tone?: "positive" | "negative";
}) {
  return (
    <StatCard
      label={label}
      value={formatMoney(figure.amount, figure.currency)}
      detail={<OtherCurrencies others={figure.others} />}
      meta={meta}
      tone={tone}
    />
  );
}
